import { useEffect, useState } from "react";

import { Modal, Typography } from "@king-ict/eupisi-ui/components";
import { useTranslation } from "react-i18next";

export enum ConfirmModalState {
  Closed = 0,
  Confirm,
  Message,
};

export interface ConfirmModalProps {
  /** title shown at the top of both modals */
  title: string;
  /** main message shown in the body of the confirm modal */
  confirmMessage?: string | undefined;
  /** smaller description shown in the body of the confirm model */
  confirmDescription?: string | undefined;
  /** only message shown in the success modal */
  successMessage?: string | undefined;
  /** state used to control visibility of modals */
  confirmState: ConfirmModalState;
  confirmButton?: string | undefined;
  cancelButton?: string | undefined;
  /** Called when the user clicks confirm */
  onClickConfirm?: () => void;
  /** Called when the user clicks cancel */
  onClickCancel?: () => void;
  /** Called after the the success message is shown */
  onClickClose?: () => void;
}

export const ConfirmModal = ({ 
  title, 
  confirmMessage, 
  confirmDescription, 
  successMessage, 
  confirmState, 
  confirmButton,
  cancelButton,
  onClickClose, 
  onClickConfirm, 
  onClickCancel 
}: ConfirmModalProps) => {
  const { t } = useTranslation();

  const [internalState, setInternalState] = useState<ConfirmModalState>(confirmState);
  const [confirmButtonText, setConfirmButtonText] = useState<string>(confirmButton ?? t("common.yes"));
  const [cancelButtonText, setCancelButtonText] = useState<string>(cancelButton ?? t("common.no"));
  const [titleText, setTitleText] = useState<string>(title);


  useEffect(() => {
    setInternalState(confirmState);
  }, [confirmState]);
  useEffect(() => {
    if (confirmButton && confirmButton.length > 0)
      setConfirmButtonText(confirmButton);
  }, [confirmButton])
  useEffect(() => {
    if (cancelButton && cancelButton.length > 0)
      setCancelButtonText(cancelButton);
  }, [cancelButton])
  useEffect(() => {
    if (title && title.length > 0)
      setTitleText(title);
  }, [title])

  const handleConfirm = () => {
    setInternalState(ConfirmModalState.Closed);
    if (onClickConfirm)
        onClickConfirm();
  };

  const handleCancel = () => {
    setInternalState(ConfirmModalState.Closed);
    if (onClickCancel)
      onClickCancel();
  };

  const handleClose = () => {
    setInternalState(ConfirmModalState.Closed);
    if (onClickClose)
      onClickClose();
  };

  return (
    <>
      {internalState === ConfirmModalState.Confirm && (
        <Modal showCloseMark
          type={"confirm"}
          title={titleText}
          cancelText={cancelButtonText}
          confirmText={confirmButtonText}
          open={internalState === ConfirmModalState.Confirm}
          onClose={handleCancel}
          onConfirm={handleConfirm}
        >
          <Typography>
            {confirmMessage}
          </Typography>
          <Typography variant="small" >
            {confirmDescription}
          </Typography>
        </Modal>
      )}
      {internalState === ConfirmModalState.Message && (
        <Modal showCloseMark
          type={"message"}
          title={titleText}
          open={internalState === ConfirmModalState.Message}
          confirmText={t("common.close")}
          onClose={handleClose}
          onConfirm={handleClose}
        >
          <Typography>
            {successMessage}
          </Typography>
        </Modal>
      )}
    </>
  );
}
